export const ATTRIBUTION_STORAGE_KEY = 'boulder_attribution';

const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ttclid',
] as const;
const MAX_ATTRIBUTION_VALUE_LENGTH = 500;
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/;

export type Attribution = Partial<Record<typeof ATTRIBUTION_PARAMS[number], string>>;

function normalizeAttributionValue(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim();
  if (
    !normalized ||
    normalized.length > MAX_ATTRIBUTION_VALUE_LENGTH ||
    CONTROL_CHARACTERS.test(normalized)
  ) {
    return undefined;
  }

  return normalized;
}

/** Returns only captured URL parameters, excluding capture metadata and empty values. */
export function readCapturedAttribution(): Attribution | undefined {
  try {
    const stored = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);

    if (!stored) return undefined;

    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return undefined;

    const attribution: Attribution = {};
    ATTRIBUTION_PARAMS.forEach((key) => {
      const value = normalizeAttributionValue((parsed as Record<string, unknown>)[key]);
      if (value) attribution[key] = value;
    });

    return Object.keys(attribution).length > 0 ? attribution : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Persists marketing attribution from the current URL so it remains available
 * during account creation on a later route.
 */
export function captureAttribution(search: string): void {
  const searchParams = new URLSearchParams(search);
  const attribution: Partial<Record<typeof ATTRIBUTION_PARAMS[number], string>> = {};

  ATTRIBUTION_PARAMS.forEach((key) => {
    const value = normalizeAttributionValue(searchParams.get(key));

    if (value) {
      attribution[key] = value;
    }
  });

  if (Object.keys(attribution).length === 0) {
    return;
  }

  try {
    window.localStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify({
        ...attribution,
        capturedAt: Date.now(),
      }),
    );
  } catch {
    // Attribution must not prevent navigation when browser storage is unavailable.
  }
}
